/**
 * The state one mounted dataset-preview grid owns, apart from the React plumbing
 * that drives it.
 *
 * Split from `grid-source` so the lifecycle this class exists to get right —
 * pure construction, subscriptions only on `connect`, teardown on disconnect —
 * can be exercised directly. Going through the hook cannot express half of it:
 * the hook always connects, so "drives nothing before connect" has no way to
 * arise there.
 */
import {
  PersistentWatchable,
  Watchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework';
import {
  createDataGridViewModel,
  dataGridColumnTypeFromSqlType,
  isDataGridRowHeight,
  DEFAULT_DATA_GRID_ROW_HEIGHT,
  type DataGridColumn,
  type DataGridColumnDisplayOptions,
  type DataGridColumnStats,
  type DataGridColumnStatsSource,
  type DataGridFilter,
  type DataGridPinnedSide,
  type DataGridRowHeight,
  type DataGridRowId,
  type DataGridSort,
  type DataGridViewModel,
} from '@cotera/client/app/components/ui/data-grid';
import { isInternalDatasetColumn } from '@cotera/sdk/core';
import type {
  ComposedDuckDbPresentation,
  DuckDbLayerStack,
} from '@cotera/client/app/etc/duckdb/layers';
import type { DatasetPreviewGridAdapter } from './adapter';
import type { DatasetPreviewRow, DatasetPreviewStatus } from './types';

/**
 * Grid presentation the user controls, persisted per dataset. This is local
 * preference rather than dataset state — it never leaves the browser, unlike
 * the saved preview query which belongs to the dataset itself.
 */
type DatasetPreviewColumnSetting = {
  id: string;
  visible: boolean;
  pinned: DataGridPinnedSide | null;
  width?: number;
};

/** Array order is the column order, so reordering is persisted for free. */
type DatasetPreviewUiSettings = {
  columns: DatasetPreviewColumnSetting[];
  sorts: DataGridSort[];
  /** Grid-wide, so it sits beside the columns rather than inside one. */
  rowHeight: DataGridRowHeight;
};

/**
 * One column's display options, keyed by name. Unlike the layout settings
 * above, these are shared dataset state persisted to the server, so they travel
 * as a callback to the model rather than through browser storage.
 */
type DatasetColumnDisplaySetting = {
  columnName: string;
  displayOptions: DataGridColumnDisplayOptions;
};

/**
 * Exactly what the persisted layout needs from storage, and no more.
 *
 * Narrower than the `StorageConnection` the provider hands out — which also
 * carries branch management — so a spec can pass three functions rather than
 * stubbing a connection or casting.
 */
type DatasetPreviewUiStorage = {
  getItem: (
    key: string,
    defaultValue: DatasetPreviewUiSettings
  ) => DatasetPreviewUiSettings;
  setItem: (key: string, value: DatasetPreviewUiSettings) => Promise<void>;
  subscribe: (
    key: string,
    listener: (value: DatasetPreviewUiSettings) => void
  ) => () => void;
};

/**
 * The filters each dataset is currently being viewed through, for this visit.
 *
 * Deliberately not in {@link DatasetPreviewUiSettings}, and deliberately not in
 * browser storage. Column widths, pinning, order, sorts and row height are
 * preferences — how you like to look at this dataset, worth keeping until you
 * change them. A filter is not: it is a question you are asking of the data
 * right now, and reopening a dataset tomorrow already narrowed to a search you
 * have forgotten making is its own bug.
 *
 * Module-level rather than held by the component, because the whole point is to
 * outlive a remount: opening the focus view, resizing the pane and switching to
 * another artifact and back all tear the grid down and build it again. A page
 * load starts fresh, which is where the visit ends.
 */
const sessionFilters = new Map<string, DataGridFilter[]>();

/**
 * The search term each dataset is currently being read through, for this visit.
 *
 * Kept beside {@link sessionFilters} and for the same reason: a search is a
 * question asked of the data right now, so it has to survive a remount — the
 * focus view, a pane resize, an artifact switched away from and back — and it
 * has to not survive a page load.
 */
const sessionSearches = new Map<string, string>();

/**
 * Drops every dataset's carried filters and search. Exists for tests, which
 * share this module and would otherwise leak one case's filters into the next
 * depending on the order they happen to run in.
 */
export const resetDatasetSessionFilters = (): void => {
  sessionFilters.clear();
  sessionSearches.clear();
};

/**
 * A width that fits the column's name, or the default where the name is short.
 *
 * Every column used to be 150px wide (180 for the first) whatever it was
 * called, so a dataset with one long name and five short ones clipped the long
 * one while the short ones sat two-thirds empty. The name is the one thing a
 * reader needs whole: a truncated value can be opened, a truncated heading just
 * leaves you guessing which column you are looking at.
 *
 * Only ever grows. Clamping the floor at the old default means no column gets
 * narrower than it is today, so a short name with long values — an `id` column
 * full of uuids — cannot be made worse by this.
 *
 * Estimated rather than measured. This runs while the view model is built,
 * before anything is on screen and in tests with no layout, so it counts
 * characters against the header's own type — 11px semibold uppercase with
 * `tracking-wide` — and adds the chrome beside the label. Being a little
 * generous is the cheap direction to be wrong in: a few px of slack costs
 * nothing, and a few px short puts the ellipsis back.
 */
const HEADER_CHROME_WIDTH = 64;
const HEADER_CHARACTER_WIDTH = 7.6;
const HEADER_FITTED_MAX_WIDTH = 320;

export const headerFittedWidth = (name: string, index: number): number => {
  const floor = index === 0 ? 180 : 150;
  const fitted = HEADER_CHROME_WIDTH + name.length * HEADER_CHARACTER_WIDTH;
  return Math.round(Math.min(HEADER_FITTED_MAX_WIDTH, Math.max(floor, fitted)));
};

const datasetPreviewUiStorageKey = (datasetId: string): string =>
  `dataset-preview-ui/${datasetId}`;

const isColumnSetting = (
  value: unknown
): value is DatasetPreviewColumnSetting => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<DatasetPreviewColumnSetting>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.visible === 'boolean' &&
    (candidate.pinned === null ||
      candidate.pinned === 'left' ||
      candidate.pinned === 'right') &&
    (candidate.width === undefined || typeof candidate.width === 'number')
  );
};

/** localStorage is untrusted input: anything malformed falls back. */
const normalizeUiSettings = (
  value: Partial<DatasetPreviewUiSettings> | null | undefined,
  fallback: DatasetPreviewUiSettings
): DatasetPreviewUiSettings => ({
  columns: Array.isArray(value?.columns)
    ? value.columns.filter(isColumnSetting)
    : fallback.columns,
  sorts: Array.isArray(value?.sorts)
    ? value.sorts.filter(
        (sort): sort is DataGridSort =>
          typeof sort?.columnId === 'string' &&
          (sort.direction === 'asc' || sort.direction === 'desc')
      )
    : fallback.sorts,
  rowHeight: isDataGridRowHeight(value?.rowHeight)
    ? value.rowHeight
    : fallback.rowHeight,
});

const columnSettingsFrom = (
  columns: DataGridColumn<DatasetPreviewRow>[]
): DatasetPreviewColumnSetting[] =>
  columns.map((column) => ({
    id: column.id,
    visible: column.visible !== false,
    pinned: column.pinned ?? null,
    width: column.width,
  }));

/**
 * Whether two settings would persist identically.
 *
 * Every persisted field has to be compared here. This is the `equalityFn` on
 * the stored watchable, so a field left out is one whose changes are silently
 * dropped rather than one that merely writes too often.
 */
const sameUiSettings = (
  left: DatasetPreviewUiSettings,
  right: DatasetPreviewUiSettings
): boolean =>
  left.columns.length === right.columns.length &&
  left.columns.every((column, index) => {
    const other = right.columns[index];
    return (
      other !== undefined &&
      column.id === other.id &&
      column.visible === other.visible &&
      column.pinned === other.pinned &&
      column.width === other.width
    );
  }) &&
  left.sorts.length === right.sorts.length &&
  left.sorts.every((sort, index) => {
    const other = right.sorts[index];
    return (
      other !== undefined &&
      sort.columnId === other.columnId &&
      sort.direction === other.direction
    );
  }) &&
  left.rowHeight === right.rowHeight;

/**
 * Per-column stat watchables. Headers subscribe to just their own column, so a
 * single column's stats resolving never re-renders the other headers.
 */
type DatasetColumnStatsStore = DataGridColumnStatsSource & {
  set(columnId: string, stats: DataGridColumnStats | undefined): void;
  clear(): void;
};

const createDatasetColumnStatsStore = (): DatasetColumnStatsStore => {
  const watchables = new Map<
    string,
    Watchable<DataGridColumnStats | undefined>
  >();
  const watchableFor = (columnId: string) => {
    const existing = watchables.get(columnId);
    if (existing !== undefined) {
      return existing;
    }
    const created = Watchable.fromValue<DataGridColumnStats | undefined>(
      undefined
    );
    watchables.set(columnId, created);
    return created;
  };

  return {
    get: watchableFor,
    set(columnId, stats) {
      watchableFor(columnId).set(stats);
    },
    clear() {
      for (const watchable of watchables.values()) {
        watchable.set(undefined);
      }
    },
  };
};

/**
 * Everything one mounted preview grid owns: its columns, its watchables, its
 * persisted layout, and the derived state that keeps the grid's own view model
 * in step with them.
 *
 * A class rather than a factory because it has a lifecycle. Construction is
 * pure — no subscriptions, no timers — and {@link connect} wires it up and
 * hands back the teardown. That split is what makes it safe under StrictMode's
 * mount/unmount/remount: a remount reconnects rather than resurrecting a
 * half-disposed object. It also fixes a real leak, because
 * `Watchable.subscribe` registers in a module-level jotai store, so a
 * subscription taken at construction and never released pinned this object for
 * the lifetime of the tab.
 */
export class DatasetPreviewGridViewModel {
  /** Keys this dataset's entry in {@link sessionFilters}. */
  private readonly datasetId: string;
  readonly grid: DataGridViewModel<DatasetPreviewRow>;
  /** The layer stack's grid columns, live channel and row detail. */
  readonly presentation: ComposedDuckDbPresentation<DatasetPreviewRow>;
  readonly rows = Watchable.fromValue<DatasetPreviewRow[]>([]);
  readonly totalRows = Watchable.fromValue<number | null>(null);
  readonly hasMore = Watchable.fromValue(false);
  readonly isLoading = Watchable.fromValue(false);
  readonly status = Watchable.fromValue<DatasetPreviewStatus>('idle');
  readonly customSelectSql: Watchable<string>;
  /**
   * The free-text term narrowing every column at once, `''` when there is none.
   *
   * Owned here rather than by the grid's filter state because the grid's
   * filters are per-column and this one is not. Debouncing belongs to whatever
   * is driving it — setting this is what runs a query, so the search box holds
   * its own draft and pushes here once the typing settles.
   */
  readonly search: Watchable<string>;
  readonly queryError = Watchable.fromValue<string | null>(null);
  /**
   * Rows patched into a value that no longer agrees with the active sort or
   * filter, so the cell is truthful while the arrangement is not.
   *
   * Kept rather than acted on: these patches come from events the user did not
   * cause, and re-querying under them would move rows and reset scroll. The
   * count drives a refresh affordance instead.
   */
  readonly driftedRowIds = Watchable.fromValue<Set<DataGridRowId>>(new Set());
  readonly columnStats = createDatasetColumnStatsStore();
  readonly queryParams: ReadonlyWatchable<{
    sorts: DataGridSort[];
    filters: DataGridFilter[];
    search: string;
    customSelectSql: string;
  }>;
  /** Everything that changes which rows exist, and therefore stats. */
  readonly populationParams: ReadonlyWatchable<{
    filters: DataGridFilter[];
    search: string;
    customSelectSql: string;
  }>;

  /**
   * The layout the user controls, persisted per dataset. Local preference
   * rather than dataset state — it never leaves the browser, unlike the saved
   * preview query, which belongs to the dataset itself.
   */
  private readonly uiSettings: Watchable<DatasetPreviewUiSettings>;
  /** Read by a layer's `renderCell`, which runs long after construction. */
  private readonly rowsHolder: { current: DatasetPreviewRow[] } = {
    current: [],
  };
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingSettings: DatasetPreviewUiSettings | null = null;
  /**
   * Persists a change to a column's display options. Undefined on viewer
   * surfaces and when no sink was given, which is what turns persistence off
   * while still letting the seed apply so shared formatting is visible.
   */
  private readonly onColumnDisplayOptionsChange:
    | ((columns: DatasetColumnDisplaySetting[]) => void)
    | undefined;
  private displayOptionsTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingDisplayOptions: Record<
    string,
    DataGridColumnDisplayOptions | undefined
  > | null = null;

  constructor(
    adapter: DatasetPreviewGridAdapter,
    layers: DuckDbLayerStack<DatasetPreviewRow>,
    options: {
      storage: DatasetPreviewUiStorage | null | undefined;
      /** Viewer surfaces read the saved layout but never write one back. */
      readOnly: boolean;
      /**
       * The display options saved on the dataset, read once at construction to
       * seed the grid. A function rather than a value so it is pulled when the
       * view model is built — which is after the artifact, and therefore the
       * settings, have loaded — rather than at an earlier render.
       */
      getInitialColumnDisplayOptions?: () => readonly DatasetColumnDisplaySetting[];
      /** Sink for display-option changes. Omitted turns persistence off. */
      onColumnDisplayOptionsChange?: (
        columns: DatasetColumnDisplaySetting[]
      ) => void;
    }
  ) {
    this.datasetId = adapter.datasetId;
    this.uiSettings = DatasetPreviewGridViewModel.loadUiSettings(
      adapter.datasetId,
      options
    );
    const stored = this.uiSettings.snapshot();
    const knownColumns = new Set(adapter.columns.map((column) => column.name));
    // Stored settings are matched against the current schema: a column can be
    // renamed or dropped between visits.
    const storedColumns = new Map(
      stored.columns
        .filter((column) => knownColumns.has(column.id))
        .map((column, index) => [column.id, { ...column, index }])
    );
    const columns: DataGridColumn<DatasetPreviewRow>[] = adapter.columns.map(
      (column, index) => {
        const saved = storedColumns.get(column.name);
        const gridType = dataGridColumnTypeFromSqlType(column.type);
        return {
          id: column.name,
          header: column.name,
          type: gridType,
          typeLabel: column.type ?? undefined,
          width: saved?.width ?? headerFittedWidth(column.name, index),
          pinned: saved?.pinned ?? null,
          sortable: true,
          filterable: true,
          // Internal bookkeeping columns are hidden rather than dropped, so
          // they stay available from the hidden-column badge in the top bar. A
          // stored preference wins, so unhiding one sticks.
          visible: saved?.visible ?? !isInternalDatasetColumn(column.name),
          // They also stay read-only: editing them would break the identity
          // every other edit is addressed by.
          editable: adapter.editable && !isInternalDatasetColumn(column.name),
          // Categorical columns get a dropdown of the values already in the
          // column, but only once that column's stats have been loaded.
          // Boolean columns are the exception: their stats are also
          // categorical, but the editor must always offer both `true` and
          // `false` regardless of which values happen to be present, so we
          // return undefined and let the editor's boolean fallback drive the
          // options.
          getEditOptions: () => {
            if (gridType === 'boolean') {
              return undefined;
            }
            const stats = this.columnStats.get(column.name).snapshot();
            return stats?.kind === 'categorical'
              ? stats.buckets.map((bucket) => bucket.label)
              : undefined;
          },
          getValue: (row) => row[column.name] ?? null,
        };
      }
    );
    // Restore the saved order. Columns added to the schema since the settings
    // were written are unknown to storage, so they keep their schema order and
    // land after everything that was saved.
    columns.sort((left, right) => {
      const leftIndex =
        storedColumns.get(left.id)?.index ?? Number.MAX_SAFE_INTEGER;
      const rightIndex =
        storedColumns.get(right.id)?.index ?? Number.MAX_SAFE_INTEGER;
      return leftIndex - rightIndex;
    });

    const gridHolder: { current: DataGridViewModel<DatasetPreviewRow> | null } =
      { current: null };
    this.presentation = layers.present({
      gridHolder,
      rowsHolder: this.rowsHolder,
      getRowId: adapter.getRowId,
    });
    this.grid = createDataGridViewModel<DatasetPreviewRow>({
      columns: [...this.presentation.columns, ...columns],
      // Expansion is only possible when a layer declares a row detail; without
      // one this is 0 and the grid keeps uniform rows. Height and renderer
      // travel together on `rowDetail`, so a renderer can no longer arrive with
      // an implicit height of zero.
      expansionHeight: this.presentation.rowDetail?.height ?? 0,
      rowHeightPreset: stored.rowHeight,
      totalRows: null,
      totalLoadedRows: 0,
    });
    gridHolder.current = this.grid;

    // Seeded before anything subscribes, so restoring a sort does not cost an
    // extra round trip on mount.
    const restoredSorts = stored.sorts.filter((sort) =>
      knownColumns.has(sort.columnId)
    );
    if (restoredSorts.length > 0) {
      this.grid.sorts.set(restoredSorts);
    }

    // Same seeding as the sorts, and the same guard: a filter on a column that
    // has been dropped or renamed since would narrow the grid to nothing with
    // no chip naming a column anyone can see.
    // Same guard the sorts get: a filter naming a column dropped or renamed
    // since would narrow the grid to nothing, with a chip pointing at a column
    // nobody can see.
    const restoredFilters = (
      sessionFilters.get(adapter.datasetId) ?? []
    ).filter((filter) => knownColumns.has(filter.columnId));
    if (restoredFilters.length > 0) {
      this.grid.filters.set(restoredFilters);
    }

    // Restore the saved display options, for viewers too — the formatting is
    // shared, so everyone who opens the dataset should see it. Seeded before
    // `connect` subscribes, so the seed itself is not read back out as a change
    // to persist. A column dropped or renamed since is skipped rather than
    // reapplied to nothing.
    this.onColumnDisplayOptionsChange = options.readOnly
      ? undefined
      : options.onColumnDisplayOptionsChange;
    const savedDisplayOptions =
      options.getInitialColumnDisplayOptions?.() ?? [];
    for (const { columnName, displayOptions } of savedDisplayOptions) {
      if (knownColumns.has(columnName)) {
        this.grid.setColumnDisplayOptions(columnName, displayOptions);
      }
    }

    this.customSelectSql = Watchable.fromValue(adapter.initialQuery);
    // No column guard, unlike the sorts and filters above: a search names no
    // column, so a schema that has moved on cannot invalidate it.
    this.search = Watchable.fromValue(
      sessionSearches.get(adapter.datasetId) ?? ''
    );
    this.queryParams = Watchable.from((get) => ({
      sorts: get(this.grid.sorts),
      filters: get(this.grid.filters),
      search: get(this.search),
      customSelectSql: get(this.customSelectSql),
    }));
    this.populationParams = Watchable.from((get) => ({
      filters: get(this.grid.filters),
      search: get(this.search),
      customSelectSql: get(this.customSelectSql),
    }));
  }

  /**
   * Wires the derived state and the layout persistence. Returns the teardown.
   *
   * Everything with a subscription or a timer lives here rather than in the
   * constructor, so nothing outlives the mount that created it.
   */
  connect(): () => void {
    const unsubscribes = [
      this.rows.subscribe((value) => {
        this.rowsHolder.current = value;
        this.grid.setTotalLoadedRows(value.length);
      }),
      this.totalRows.subscribe((value) => this.grid.setTotalRows(value)),
      // Persist visibility, pinning, width, order and sorts so the grid comes
      // back in the shape it was left in.
      this.grid.columns.subscribe((columns) =>
        this.queueSettings({
          ...this.currentSettings(),
          columns: columnSettingsFrom(columns),
        })
      ),
      // Written through rather than queued. The debounce exists for column
      // resizing, which fires on every mousemove; a sort, a filter and a row
      // height are single deliberate acts, and delaying them by 250ms means a
      // reader who filters and immediately hits the focus view — a route
      // change, so the grid remounts and reads storage back — gets the state
      // from before the filter. That is the whole reported bug.
      this.grid.sorts.subscribe((sorts) =>
        this.writeSettings({ ...this.currentSettings(), sorts })
      ),
      // Straight to the visit-scoped store, and unconditionally: clearing the
      // filters has to be recorded as surely as setting them, or the next mount
      // restores the ones just removed.
      this.grid.filters.subscribe((filters) =>
        sessionFilters.set(this.datasetId, filters)
      ),
      // Same store, same unconditional write: clearing the search has to be
      // recorded as surely as setting it, or the next mount restores the term
      // just deleted.
      this.search.subscribe((search) =>
        sessionSearches.set(this.datasetId, search)
      ),
      this.grid.rowHeightPreset.subscribe((rowHeight) =>
        this.writeSettings({ ...this.currentSettings(), rowHeight })
      ),
    ];

    // Only when there is somewhere to persist to: viewer surfaces and the picker
    // leave the sink undefined, and subscribing there would only debounce writes
    // that go nowhere.
    if (this.onColumnDisplayOptionsChange !== undefined) {
      unsubscribes.push(
        this.grid.columnDisplayOptions.subscribe((options) =>
          this.queueDisplayOptions(options)
        )
      );
    }

    return () => {
      for (const unsubscribe of unsubscribes) {
        unsubscribe();
      }
      if (this.persistTimer !== null) {
        clearTimeout(this.persistTimer);
        this.persistTimer = null;
      }
      if (this.displayOptionsTimer !== null) {
        clearTimeout(this.displayOptionsTimer);
        this.displayOptionsTimer = null;
      }
      // Leaving the page must not drop the most recent change.
      this.flushSettings();
      this.flushDisplayOptions();
    };
  }

  /** What the grid looks like right now, as the settings would record it. */
  private currentSettings(): DatasetPreviewUiSettings {
    return {
      columns: columnSettingsFrom(this.grid.columns.snapshot()),
      sorts: this.grid.sorts.snapshot(),
      rowHeight: this.grid.rowHeightPreset.snapshot(),
    };
  }

  /**
   * Debounced because a column resize fires on every mousemove and
   * localStorage writes are synchronous.
   */
  private queueSettings(next: DatasetPreviewUiSettings): void {
    this.pendingSettings = next;
    if (this.persistTimer !== null) {
      clearTimeout(this.persistTimer);
    }
    this.persistTimer = setTimeout(() => this.flushSettings(), 250);
  }

  /**
   * Persists now, superseding anything the debounce was still holding.
   *
   * Safe to supersede because `currentSettings` reads the grid rather than the
   * pending object, so a queued column resize is folded into this write instead
   * of being dropped by it.
   */
  private writeSettings(next: DatasetPreviewUiSettings): void {
    if (this.persistTimer !== null) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
    this.pendingSettings = next;
    this.flushSettings();
  }

  private flushSettings(): void {
    const pending = this.pendingSettings;
    this.pendingSettings = null;
    if (pending !== null) {
      this.uiSettings.set(pending);
    }
  }

  /**
   * Debounced because a change fires on every click inside the display-options
   * dialog — a format, then decimals, then alignment — and each would otherwise
   * be its own network write.
   */
  private queueDisplayOptions(
    next: Record<string, DataGridColumnDisplayOptions | undefined>
  ): void {
    this.pendingDisplayOptions = next;
    if (this.displayOptionsTimer !== null) {
      clearTimeout(this.displayOptionsTimer);
    }
    this.displayOptionsTimer = setTimeout(
      () => this.flushDisplayOptions(),
      250
    );
  }

  private flushDisplayOptions(): void {
    const pending = this.pendingDisplayOptions;
    this.pendingDisplayOptions = null;
    if (this.displayOptionsTimer !== null) {
      clearTimeout(this.displayOptionsTimer);
      this.displayOptionsTimer = null;
    }
    if (pending === null || this.onColumnDisplayOptionsChange === undefined) {
      return;
    }
    // The whole current set, not a diff: the sink upserts, so re-sending an
    // unchanged column is harmless and keeps this from having to track which
    // columns moved since the last write.
    const columns: DatasetColumnDisplaySetting[] = Object.entries(pending)
      .filter(
        (entry): entry is [string, DataGridColumnDisplayOptions] =>
          entry[1] !== undefined
      )
      .map(([columnName, displayOptions]) => ({ columnName, displayOptions }));
    this.onColumnDisplayOptionsChange(columns);
  }

  /**
   * Moves a renamed column's saved layout onto its new name.
   *
   * The layout keys columns by name, and a rename rebuilds the adapter — so
   * without this the renamed column is unknown to the stored settings on the
   * very next build: it loses its width, its pinning and, because unknown
   * columns sort after known ones, its place in the order. It jumps to the end
   * of the grid for no reason the user can connect to what they just did.
   *
   * Static because the rename is what destroys the instance holding these: the
   * settings have to be moved before the next view model reads them.
   */
  static renameStoredColumn(options: {
    storage: DatasetPreviewUiStorage | null | undefined;
    datasetId: string;
    oldName: string;
    newName: string;
  }): void {
    const { storage, datasetId, oldName, newName } = options;
    if (storage === null || storage === undefined) {
      return;
    }
    const key = datasetPreviewUiStorageKey(datasetId);
    const fallback: DatasetPreviewUiSettings = {
      columns: [],
      sorts: [],
      rowHeight: DEFAULT_DATA_GRID_ROW_HEIGHT,
    };
    const current = storage.getItem(key, fallback);
    if (!current.columns.some((column) => column.id === oldName)) {
      return;
    }
    void storage.setItem(key, {
      ...current,
      columns: current.columns.map((column) =>
        column.id === oldName ? { ...column, id: newName } : column
      ),
      // A sort on the renamed column is still the sort the user asked for.
      sorts: current.sorts.map((sort) =>
        sort.columnId === oldName ? { ...sort, columnId: newName } : sort
      ),
    });
  }

  private static loadUiSettings(
    datasetId: string,
    options: {
      storage: DatasetPreviewUiStorage | null | undefined;
      readOnly: boolean;
    }
  ): Watchable<DatasetPreviewUiSettings> {
    // No saved layout: every column falls back to its default, which hides the
    // internal bookkeeping columns.
    const fallback: DatasetPreviewUiSettings = {
      columns: [],
      sorts: [],
      rowHeight: DEFAULT_DATA_GRID_ROW_HEIGHT,
    };
    const storage = options.storage;
    if (storage === null || storage === undefined) {
      return Watchable.fromValue(fallback);
    }
    const persisted = PersistentWatchable.fromStorage<DatasetPreviewUiSettings>(
      {
        storage,
        storageKey: datasetPreviewUiStorageKey(datasetId),
        defaultValue: fallback,
        map: (value) => normalizeUiSettings(value, fallback),
        equalityFn: sameUiSettings,
      }
    );
    // A viewer opens on the saved layout but never writes one back: resizing or
    // sorting inside a picker should not rearrange the dataset page.
    return options.readOnly
      ? Watchable.fromValue(persisted.snapshot())
      : persisted;
  }
}
